import React, { FC, useEffect, useRef } from 'react';
import { SwiperProps } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import Slider from 'components/commercetools-ui/atoms/slider';
import Subtitle, { SubtitleProps } from 'components/commercetools-ui/atoms/subtitle';
import ProductTile from 'components/commercetools-ui/molecules/product-tile';
import Wrapper from 'components/HOC/wrapper';
import { trackViewItemList, trackSelectItem, mapProductToGA4Item } from 'helpers/analytics/ga4Events';
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { mediumDesktop, tablet } from 'helpers/utils/screensizes';
import { useFeatures } from 'providers/feature-flag';
import { ShippingMethod } from 'types/entity/cart';
import { Product, Variant } from 'types/entity/product';
import { LineItem, Wishlist } from 'types/entity/wishlist';
import { Reference } from 'types/reference';
import Link from '../../content/link';

export interface ProductSliderProps extends Partial<SwiperProps> {
  products: Product[];
  wishlist?: Wishlist;
  shippingMethods?: ShippingMethod[];
  title: string;
  titleVariant?: 'xs' | 'sm' | 'lg';
  subline?: string;
  subtitleVariant?: SubtitleProps['variant'];
  ctaLabel?: string;
  ctaLink?: Reference;
  clearDefaultWrapperStyles?: boolean;
  slidesPerView?: number;
  disableProductQuickView?: boolean;
  disableProductWishlistButton?: boolean;
  //disableProductVariants?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  onProductClick?: (product: Product) => void;
  classNames?: {
    title?: string;
  };
  variant?: 'normal' | 'cart';
  addToWishlist?: (lineItem: LineItem, count: number) => Promise<void>;
  removeLineItem?: (item: LineItem) => Promise<void>;
  onAddToCart?: (variant: Variant, quantity: number) => Promise<void>;
}

const ProductSlider: FC<ProductSliderProps> = ({
  products,
  wishlist,
  shippingMethods,
  title,
  subline,
  ctaLabel,
  ctaLink,
  titleVariant = 'lg',
  subtitleVariant = 'lg',
  clearDefaultWrapperStyles = false,
  disableProductQuickView = false,
  disableProductWishlistButton = false,
  //disableProductVariants = false,
  breakpoints = {},
  classNames = {},
  addToWishlist,
  removeLineItem,
  onProductClick,
  onAddToCart,
  spaceBetween = 8,
  ...props
}) => {
  const { isTouchDevice } = useTouchDevice();

  const titleClassName = useClassNames([
    'mb-12 text-20',
    classNames.title,
    {
      xs: 'md:text-16',
      sm: 'md:text-18 lg:text-22',
      lg: 'md:text-22 lg:text-28',
    }[titleVariant ?? 'lg'],
  ]);

  // GA4: view_item_list — fires when the slider's products are loaded
  const viewItemListFired = useRef<string>('');
  const {enableGA4} = useFeatures();
  useEffect(() => {
    if (products.length > 0) {
      const productIds = products.map((p) => p.productId).join(',');
      if (productIds !== viewItemListFired.current) {
        const listName = title || 'Product Carousel';
        const items = products.map((product, index) =>
          mapProductToGA4Item(enableGA4(),product, { index, itemListName: listName }),
        );
        trackViewItemList(listName, items);
        viewItemListFired.current = productIds;
      }
    }
  }, [products, title]);

  return (
    <Wrapper background="neutral-200" clearDefaultStyles={clearDefaultWrapperStyles}>
      <div>
        <p className={titleClassName}>{title}</p>
        {(subline || ctaLink) && (
          <div className="mt-8 flex items-center justify-between md:mt-16 lg:mt-14">
            {subline && <Subtitle subtitle={subline} variant={subtitleVariant} />}
            {ctaLink && (
              <div className="hidden lg:block">
                <Link target={ctaLink} withArrow>
                  <span className="font-medium leading-[24px] text-gray-600">{ctaLabel}</span>
                </Link>
              </div>
            )}
          </div>
        )}
        {ctaLink && (
          <div className="mt-20 block md:mt-16 lg:hidden">
            <Link target={ctaLink} withArrow>
              <span className="font-medium leading-[24px] text-gray-600">{ctaLabel}</span>
            </Link>
          </div>
        )}
      </div>
      <div className="pt-20 md:pt-24 lg:pt-20">
        <Slider
          slidesPerView={isTouchDevice ? 2.3 : 1.3}
          slidesPerGroup={1}
          dots={false}
          arrows
          allowTouchMove
          spaceBetween={spaceBetween}
          breakpoints={{
            [tablet]: {
              slidesPerView: 2.3,
              spaceBetween: 25,
              ...breakpoints[tablet],
            },
            [mediumDesktop]: {
              slidesPerView: 4,
              slidesPerGroup: 4,
              spaceBetween: 25,
              ...breakpoints[mediumDesktop],
            },
          }}
          {...props}
        >
          {products.map((product, index) => (
            <ProductTile
              key={product.productId}
              wishlist={wishlist}
              shippingMethods={shippingMethods}
              disableQuickView={disableProductQuickView}
              disableWishlistButton={disableProductWishlistButton}
              product={product}
              onClick={() => {
                onProductClick?.(product);
                // GA4: select_item
                const listName = title || 'Product Carousel';
                trackSelectItem(listName, [
                  mapProductToGA4Item(enableGA4(), product, { index, itemListName: listName }),
                ]);
              }}
              addToWishlist={addToWishlist}
              removeLineItem={removeLineItem}
              onAddToCart={onAddToCart}
              index={index}
            />
          ))}
        </Slider>
      </div>
    </Wrapper>
  );
};

export default ProductSlider;
